import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { unstable_usePrompt } from 'react-router-dom';

import { FormHelperText } from '@mui/material';
import { useAtom } from 'jotai';

import { SKIP_PROMPT_STATE_KEY } from 'common/hooks';

import { productMenuDisabledAtom } from 'api/gui';

export function CommonFormComponents() {
    return (
        <>
            <DisableProductMenu />
            <WarnOnUnsavedChanges />
        </>
    );
}

export function DisableProductMenu() {
    const [, setProductMenuDisabled] = useAtom(productMenuDisabledAtom);
    useEffect(() => {
        setProductMenuDisabled(true);
        return () => {
            setProductMenuDisabled(false);
        };
    }, [setProductMenuDisabled]);

    return null;
}

const MESSAGE = 'You have unsaved changes. Are you sure you want to leave?';

const eventListener = event => {
    event.preventDefault();
    return (event.returnValue = MESSAGE);
};

function WarnOnUnsavedChanges() {
    const {
        formState: { isDirty, isSubmitting },
    } = useFormContext();

    // Actually leaving the page (e.g. refreshing or closing the tab)
    useEffect(() => {
        if (!isSubmitting && isDirty) {
            window.addEventListener('beforeunload', eventListener);
            return () => window.removeEventListener('beforeunload', eventListener);
        }

        window.removeEventListener('beforeunload', eventListener);
        return undefined;
    });

    // Going to another route within the single-page app
    // quick replacement for the v5 Prompt component:
    unstable_usePrompt({
        when: ({ nextLocation }) => {
            const skip = (nextLocation.state as any)?.[SKIP_PROMPT_STATE_KEY] === true;
            return !skip && isDirty && !isSubmitting;
        },
        message: MESSAGE,
    });

    return null;
}

export function NonFieldErrors({ className }: { className?: string }) {
    const {
        formState: { errors },
    } = useFormContext();
    return (
        <FormHelperText error className={className}>
            {String(errors.non_field_errors?.message || errors.root?.serverError?.message || '')}
        </FormHelperText>
    );
}
